import React from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTTS } from "@/hooks/useTTS";

/**
 * Small "Écouter"/"Arrêter" button used across DishCard, RestaurantCard, ReviewsList.
 * Uses the shared useTTS hook (browser-side cache included).
 *
 * @param {string} text – text to synthesize
 * @param {string} testId – data-testid for QA (e.g. "dish-tts-{id}")
 * @param {string} [size] – "sm" | "xs" (default "sm")
 * @param {string} [ariaLabel]
 */
export default function TTSButton({ text, testId, size = "sm", ariaLabel }) {
  const { t } = useTranslation();
  const { state, error, play } = useTTS(text);
  const label = state === "loading" ? t("tts.loading") : state === "playing" ? t("tts.stop") : t("tts.listen");

  const pad = size === "xs" ? "px-2 py-0.5" : "px-2.5 py-1";
  const txt = size === "xs" ? "text-[11px]" : "text-xs";
  const ico = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <div className="inline-flex flex-col items-start">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); play(); }}
        disabled={state === "loading"}
        data-testid={testId}
        aria-label={ariaLabel || label}
        className={`inline-flex items-center gap-1.5 ${txt} font-medium rounded-full ${pad} border transition-colors ${
          state === "playing"
            ? "border-brand-red bg-brand-red/10 text-brand-red"
            : "border-sand text-mute hover:border-brand-red hover:text-brand-red bg-white"
        } disabled:opacity-60`}
      >
        {state === "loading" && <Loader2 className={`${ico} animate-spin`} />}
        {state === "playing" && <Square className={ico} />}
        {state === "idle" && <Volume2 className={ico} />}
        <span>{label}</span>
      </button>
      {error && (
        <span data-testid={`${testId}-error`} className="mt-0.5 text-[10px] text-brand-red">{error}</span>
      )}
    </div>
  );
}
